import type { ApiErrorResponse, AuthSession } from '@ldembalagens/types';
import { authStore } from '@/stores/auth-store';

export const API_BASE_PATH =
  (import.meta.env.VITE_API_BASE_PATH as string | undefined) ?? '/api/v1';
const API_ORIGIN = import.meta.env.DEV
  ? ''
  : ((import.meta.env.VITE_API_URL as string | undefined) ?? '');

/** Erro de API com o payload padronizado do backend. */
export class ApiError extends Error {
  readonly status: number;
  readonly body: ApiErrorResponse | null;

  constructor(status: number, body: ApiErrorResponse | null) {
    super(body?.message ?? `Erro ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }

  get code(): string | undefined {
    return this.body?.code;
  }

  get fields(): ApiErrorResponse['fields'] {
    return this.body?.fields;
  }

  static is(error: unknown, code?: string): error is ApiError {
    return error instanceof ApiError && (code === undefined || error.code === code);
  }
}

export type QueryParams = Record<string, string | number | boolean | undefined | null>;

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: QueryParams;
  signal?: AbortSignal;
  /** Não tenta renovar o token em 401 (usado pelos próprios endpoints de auth). */
  skipAuthRefresh?: boolean;
  /** Cabeçalhos adicionais (ex.: Idempotency-Key). */
  headers?: Record<string, string>;
}

export function buildUrl(path: string, query?: QueryParams): string {
  const url = `${API_ORIGIN}${API_BASE_PATH}${path.startsWith('/') ? path : `/${path}`}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

const CSRF_HEADERS = { 'X-Requested-With': 'XMLHttpRequest' } as const;

let refreshInFlight: Promise<boolean> | null = null;

/**
 * Renova a sessão usando o cookie HttpOnly. Chamadas concorrentes compartilham
 * a mesma promessa (single-flight) para evitar rotação dupla do refresh token.
 */
export function refreshSession(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      try {
        const res = await fetch(buildUrl('/auth/refresh'), {
          method: 'POST',
          credentials: 'include',
          headers: { ...CSRF_HEADERS },
        });
        if (!res.ok) {
          authStore.getState().clear();
          return false;
        }
        authStore.getState().setSession((await res.json()) as AuthSession);
        return true;
      } catch {
        authStore.getState().clear();
        return false;
      } finally {
        refreshInFlight = null;
      }
    })();
  }
  return refreshInFlight;
}

async function parseError(res: Response): Promise<ApiError> {
  const body = await res
    .json()
    .then((b: unknown) => b as ApiErrorResponse)
    .catch(() => null);
  return new ApiError(res.status, body);
}

/** Cliente HTTP central: Bearer token em memória + refresh automático em 401. */
export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const execute = async (): Promise<Response> => {
    const token = authStore.getState().accessToken;
    const headers: Record<string, string> = {
      Accept: 'application/json',
      ...CSRF_HEADERS,
      ...(options.headers ?? {}),
    };
    const isForm = options.body instanceof FormData;
    if (options.body !== undefined && !isForm) headers['Content-Type'] = 'application/json';
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return fetch(buildUrl(path, options.query), {
      method: options.method ?? 'GET',
      headers,
      credentials: 'include',
      body: isForm
        ? (options.body as FormData)
        : options.body !== undefined
          ? JSON.stringify(options.body)
          : undefined,
      signal: options.signal,
    });
  };

  let res = await execute();

  if (res.status === 401 && !options.skipAuthRefresh) {
    const refreshed = await refreshSession();
    if (refreshed) res = await execute();
  }

  if (!res.ok) throw await parseError(res);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  get: <T>(path: string, query?: QueryParams, signal?: AbortSignal) =>
    apiFetch<T>(path, { query, signal }),
  post: <T>(path: string, body?: unknown) => apiFetch<T>(path, { method: 'POST', body }),
  patch: <T>(path: string, body?: unknown) => apiFetch<T>(path, { method: 'PATCH', body }),
  delete: <T = void>(path: string) => apiFetch<T>(path, { method: 'DELETE' }),
};

/**
 * Download autenticado: o token vive em memória (não em cookie), então o arquivo
 * é obtido via fetch com Bearer e entregue ao navegador como blob.
 */
async function fetchFile(
  path: string,
  query?: QueryParams,
): Promise<{ blob: Blob; fileName: string }> {
  const execute = () =>
    fetch(buildUrl(path, query), {
      headers: {
        ...CSRF_HEADERS,
        Authorization: `Bearer ${authStore.getState().accessToken ?? ''}`,
      },
      credentials: 'include',
    });
  let res = await execute();
  if (res.status === 401 && (await refreshSession())) res = await execute();
  if (!res.ok) throw await parseError(res);
  const disposition = res.headers.get('content-disposition') ?? '';
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  const fileName = match?.[1] ? decodeURIComponent(match[1]) : 'arquivo';
  return { blob: await res.blob(), fileName };
}

export async function downloadFile(path: string, query?: QueryParams): Promise<void> {
  const { blob, fileName } = await fetchFile(path, query);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/**
 * Abre o arquivo autenticado (ex.: PDF) numa nova aba, para visualizar e imprimir.
 * A aba é aberta antes da requisição para não ser barrada pelo bloqueador de pop-ups.
 */
export async function openFile(path: string, query?: QueryParams): Promise<void> {
  const win = window.open('', '_blank');
  try {
    const { blob, fileName } = await fetchFile(path, query);
    const url = URL.createObjectURL(blob);
    if (win) {
      win.location.href = url;
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } else {
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    }
  } catch (error) {
    win?.close();
    throw error;
  }
}
